import type { BookableResourceRef } from "./bookable-resource";
import type { CodeableConcept, Period } from "./common";
import { PreconditionFailedError } from "./errors";
import type { HealthcareServiceId } from "./healthcare-service.model";
import type { Brand } from "./lib/brand";
import type { DomainId } from "./lib/domain-id";
import { type MemberId, memberIdSequence } from "./member-identity";
import { TimeRange } from "./time-range";

/** Stable Schedule domain identity, independent of the FHIR Schedule locator. */
export type ScheduleId = DomainId<"ScheduleId">;

export function asScheduleId(value: string): ScheduleId {
  if (!value) throw new PreconditionFailedError("A Schedule identity cannot be empty");
  return value as ScheduleId;
}

/** A block's member identity within its Schedule: a MemberId, told apart from other members'. */
export type ScheduleBlockId = Brand<MemberId, "ScheduleBlockId">;

export function asScheduleBlockId(value: string): ScheduleBlockId {
  if (!/^block-[1-9]\d*$/.test(value)) throw new PreconditionFailedError(`'${value}' is not a Schedule block identity`);
  return value as ScheduleBlockId;
}

// ============================================================
// Schedule — one resource's calendar (ADR-0017)
//
// A clinician, a room or a machine has exactly one schedule, and it holds two
// quite different things that FHIR spells the same way.
//
// THE BLOCKS ARE MEMBERS; THE BOOKINGS ARE NOT. Both are Slots on this
// Schedule, but a `busy-unavailable` slot is a block this calendar's owner put
// there (lunch, vacation) and belongs to this aggregate, while a `busy` slot is
// a reservation made BY a visit and belongs to that visit — see
// [[visit.model]], which writes and frees them. So `blocks` below is only ever
// the unavailability, and the mapper's member query is filtered accordingly.
//
// That is also why this aggregate is small. Bookings accumulate forever;
// blocks are a handful per calendar.
//
// Closing is refused while any booking still stands, and the count comes from
// the caller because those bookings are other aggregates.
// ============================================================

export type ScheduleActor = BookableResourceRef;
export const SCHEDULE_DAYS_OF_WEEK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export const SCHEDULE_DURATION_UNITS = ["min", "h"] as const;
export const SCHEDULE_RECURRENCE_FREQUENCIES = ["daily", "weekly"] as const;

type DayOfWeek = (typeof SCHEDULE_DAYS_OF_WEEK)[number];

interface Duration {
  value: number;
  unit: (typeof SCHEDULE_DURATION_UNITS)[number];
}

/**
 * A repeating block, stored as one template slot pinned to a sentinel date with
 * the rule carried alongside it — rather than as thousands of materialised
 * occurrences.
 */
interface Recurrence {
  frequency: (typeof SCHEDULE_RECURRENCE_FREQUENCIES)[number];
  dayOfWeek?: DayOfWeek[];
  startDate?: string;
  endDate?: string;
}

/** Time this calendar's owner is not available. */
export interface ScheduleBlock {
  /** Stable parent-scoped member identity, independent of Slot.id. */
  blockId: ScheduleBlockId;
  start: string;
  end: string;
  reason?: string;
  recurring?: Recurrence;
}

interface Availability {
  dayOfWeek: DayOfWeek[];
  startTime: string;
  endTime: string;
}

/** What a service IS — the coding a calendar is searched by. */
export interface ServiceTypeCode {
  system: string;
  code: string;
  display?: string;
}

export interface ScheduleServiceType extends CodeableConcept {
  /** Stable identity of the HealthcareService this searchable type represents. */
  serviceId?: HealthcareServiceId;
}

/** How one bookable service behaves on this calendar. */
export interface ServiceParameters {
  serviceId: HealthcareServiceId;
  duration: Duration;
  timezone?: string;
  availability?: Availability;
  bufferBefore?: Duration;
  bufferAfter?: Duration;
  alignmentInterval?: Duration;
  alignmentOffset?: Duration;
  bookingAdvanceLimit?: { value: number; unit: "d" | "wk" };
  bookingLimit?: Array<{ maxPerPeriod: number; periodUnit: "d" | "wk" }>;
}

export interface ScheduleProps {
  id: ScheduleId;
  active: boolean;
  actor: ScheduleActor;
  planningHorizon?: Period;
  serviceCategory?: CodeableConcept[];
  serviceType?: ScheduleServiceType[];
  specialty?: CodeableConcept[];
  comment?: string;
  services: ServiceParameters[];
  /** Unavailability only. Bookings are the visits' slots and are not members here. */
  blocks: ScheduleBlock[];
  /** Highest block member number ever issued; prevents reuse after deletion. */
  blockIdHighWaterMark: number;
}

export type StoredScheduleProps = Omit<ScheduleProps, "active"> & { active?: boolean };

export type CreateScheduleInput = Omit<ScheduleProps, "blocks" | "blockIdHighWaterMark"> & {
  blocks: Array<Omit<ScheduleBlock, "blockId">>;
};

/**
 * One calendar per actor. Whether the actor already has one is a fact about
 * ANOTHER aggregate, so it is passed in — the caller runs the search, this
 * decides what the answer means.
 */
export function assertCalendarAvailable(actor: ScheduleActor, facts: { taken: boolean }): void {
  if (facts.taken) {
    throw new PreconditionFailedError(`${actor.type} '${actor.id}' already has a schedule`);
  }
}

export class Schedule {
  private state: ScheduleProps;

  private constructor(props: ScheduleProps) {
    if (!props.actor.id) {
      throw new PreconditionFailedError("A schedule requires an actor");
    }
    for (const block of props.blocks) assertWellFormed(block);
    this.state = structuredClone(props);
  }

  static create(input: CreateScheduleInput): Schedule {
    const ids = memberIdSequence("block");
    const blocks = input.blocks.map((block) => ({ ...block, blockId: asScheduleBlockId(ids.next()) }));
    return new Schedule({
      ...structuredClone(input),
      id: asScheduleId(input.id),
      blocks,
      blockIdHighWaterMark: ids.high,
    });
  }

  static restore(stored: StoredScheduleProps): Schedule {
    // An unflagged stored schedule is not open for booking; availability is
    // asserted, never inferred from a row that never said either way.
    const snapshot = { ...stored, active: stored.active ?? false };
    const derivedHigh = snapshot.blocks.reduce(
      (high, block) => Math.max(high, Number(block.blockId.slice("block-".length))),
      0,
    );
    if (snapshot.blockIdHighWaterMark < derivedHigh) {
      throw new PreconditionFailedError("Schedule block identity high-water mark is behind its live blocks");
    }
    return new Schedule(snapshot);
  }

  // ── facts ──

  get id(): ScheduleId {
    return this.state.id;
  }

  get isActive(): boolean {
    return this.state.active;
  }

  get actorRef(): string {
    return [this.state.actor.type, this.state.actor.id].join("/");
  }

  get blocks(): readonly ScheduleBlock[] {
    return structuredClone(this.state.blocks);
  }

  /** Whether this calendar already offers a service. */
  offers(serviceId: HealthcareServiceId): boolean {
    return this.state.services.some((s) => s.serviceId === serviceId);
  }

  snapshot(): ScheduleProps {
    return structuredClone(this.state);
  }

  // ── availability ──

  /** Apply the actor’s working pattern while retaining service-specific booking settings. */
  adjustAvailability(changes: { availability?: Availability; timezone?: string }): void {
    for (const service of this.state.services) {
      if (changes.availability !== undefined) service.availability = structuredClone(changes.availability);
      if (changes.timezone !== undefined) service.timezone = changes.timezone;
    }
  }

  /** Mark time the owner is unavailable. */
  block(block: Omit<ScheduleBlock, "blockId">): ScheduleBlockId {
    if (!this.state.active) {
      throw new PreconditionFailedError("Cannot block availability on an inactive schedule");
    }
    assertWellFormed(block);
    const ids = memberIdSequence("block", this.state.blockIdHighWaterMark);
    const blockId = asScheduleBlockId(ids.next());
    this.state.blockIdHighWaterMark = ids.high;
    this.state.blocks.push({ ...block, blockId });
    return blockId;
  }

  /** Release a block. Bookings are not blocks and cannot be released this way. */
  unblock(blockId: ScheduleBlockId): void {
    const index = this.state.blocks.findIndex((b) => b.blockId === blockId);
    if (index === -1) {
      throw new PreconditionFailedError(`Block '${blockId}' is not on this schedule`);
    }
    this.state.blocks.splice(index, 1);
  }

  /**
   * Close the calendar. `bookedSlotCount` comes from the caller because those
   * bookings belong to visits, not to this aggregate — closing under a live
   * booking would strand a patient with an appointment on a dead calendar.
   */
  close(bookedSlotCount: number): void {
    if (bookedSlotCount > 0) {
      throw new PreconditionFailedError(
        `Cannot close this schedule: ${bookedSlotCount} slot(s) are booked by active appointments`,
      );
    }
    this.state.active = false;
  }

  /**
   * Correct the calendar's own description. A key that is present is applied
   * even when its value is `undefined` — that is how a caller clears a field.
   *
   * `actor` and `blocks` are deliberately outside the change set. Moving a
   * calendar to another actor would silently re-own every booking already on
   * it, and blocks move one at a time through `block` and `unblock` so each is
   * checked for being well-formed. `services` IS replaceable wholesale: the
   * caller sends the whole list, which is how a service is removed.
   */
  revise(changes: Partial<Omit<ScheduleProps, "id" | "actor" | "blocks" | "blockIdHighWaterMark">>): void {
    // Stripped at RUNTIME as well as in the signature. The type keeps the three
    // out of a literal, but a spread of some wider object carries them through
    // and a plain merge would then apply them.
    const { id, actor, blocks, blockIdHighWaterMark, ...safe } = changes as Partial<ScheduleProps>;
    Object.assign(this.state, safe);
  }

  // ── the services this calendar offers ──

  /**
   * Offer a service on this calendar, or correct how long it takes here.
   * Idempotent when nothing changes.
   *
   * Timezone and weekly availability are copied from a service already on the
   * calendar: they describe the ACTOR's working pattern, not the service, so a
   * newly offered service inherits rather than arriving with none.
   *
   * `typeCode` is what the service IS, and it is recorded separately in
   * `serviceType` because that is the field FHIR indexes — the scheduling
   * parameters live in an extension, which nothing can search. Omit it and the
   * calendar still offers the service; it just cannot be found by it.
   */
  offerService(params: ServiceParameters, typeCode?: ServiceTypeCode): void {
    const existing = this.state.services.find((s) => s.serviceId === params.serviceId);
    if (existing) {
      if (sameDuration(existing.duration, params.duration)) return;
      existing.duration = params.duration;
      return;
    }
    const sibling = this.state.services[0];
    this.state.services.push({
      ...params,
      timezone: params.timezone ?? sibling?.timezone,
      availability: params.availability ?? sibling?.availability,
      // A service with no cadence of its own is offered at its own duration —
      // otherwise the scheduler has no interval to align starts to.
      alignmentInterval: params.alignmentInterval ?? params.duration,
    });
    if (typeCode) {
      this.state.serviceType = [...(this.state.serviceType ?? []), serviceTypeEntry(params.serviceId, typeCode)];
    }
  }

  withdrawService(serviceId: HealthcareServiceId): void {
    this.state.services = this.state.services.filter((s) => s.serviceId !== serviceId);
    this.state.serviceType = (this.state.serviceType ?? []).filter((entry) => referencedServiceId(entry) !== serviceId);
  }
}

/**
 * The reference back to the service, on the coding that names it. Medplum's own
 * extension url — a Schedule.serviceType coding carries the code, and this is
 * what says WHICH service that code belongs to.
 */
function serviceTypeEntry(serviceId: HealthcareServiceId, typeCode: ServiceTypeCode): ScheduleServiceType {
  return {
    coding: [{ system: typeCode.system, code: typeCode.code, ...(typeCode.display && { display: typeCode.display }) }],
    serviceId,
  };
}

function referencedServiceId(entry: ScheduleServiceType): HealthcareServiceId | undefined {
  return entry.serviceId;
}

function assertWellFormed(block: Pick<ScheduleBlock, "start" | "end">): void {
  try {
    new TimeRange(new Date(block.start), new Date(block.end));
  } catch {
    throw new PreconditionFailedError("A block must start before it ends");
  }
}

function sameDuration(a: Duration, b: Duration): boolean {
  return a.value === b.value && a.unit === b.unit;
}
